import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import useAuth from "../../../../hooks/account/useAuth";
import { CreateCommunityForm } from "../../../../types/forms";
import { SubscriptionType, BusinessSubscriptionType } from "../../../../types/misc";
import { featuresInSubscription, getMinimumSubscriptionForFeatures } from "../../../../utils/FeatureUtils";
import { isSubscriptionGreaterThanOrEqual } from "../../../../utils/subscriptionUtils";
import communityService from "../../../../services/community/communityService";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import { SubscriptionSelector } from "../../../components/selection-lists/SubscriptionSelector";
import AreYouSureModal from "../../../components/modals/AreYouSureModal";
import { RootState } from "../../../../redux/reducers";

type SubCommunitySubscriptionViewProps = {
  setStep: (step: 1 | -1) => void;
  community: CreateCommunityForm;
  setCommunity: (community: CreateCommunityForm) => void;
}

export default function SubCommunitySubscriptionView({ 
  community, 
  setCommunity, 
  setStep 
}: SubCommunitySubscriptionViewProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [downgradeWarningOpen, setDowngradeWarningOpen] = useState(false);
  const [pendingSubscription, setPendingSubscription] = useState<SubscriptionType | null>(null);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  const selectedBusinessId = useSelector((state: RootState) => state.application.selectedBusiness);
  const communityMode = params.get('mode') === 'community';

  const disableIncompatibleFeatures = () => {
    const selectedFeatures = { ...community.features };
    const allowedFeatures = featuresInSubscription[pendingSubscription ?? SubscriptionType.FREE];
    
    Object.keys(selectedFeatures).forEach((key) => {
      if (!allowedFeatures[key as keyof typeof allowedFeatures]) {
        selectedFeatures[key as keyof typeof selectedFeatures] = false;
      }
    });
    return selectedFeatures;
  };

  const handleSubscriptionSelection = (subscriptionType: SubscriptionType | BusinessSubscriptionType) => {
    // Cast to SubscriptionType since we know isBusiness is false
    const communitySubscription = subscriptionType as SubscriptionType;
    const requiredSubscription = getMinimumSubscriptionForFeatures(community.features || {});
    
    const isValidSubscription = isSubscriptionGreaterThanOrEqual(communitySubscription, requiredSubscription);
    
    if (!isValidSubscription) {
      setPendingSubscription(communitySubscription);
      setDowngradeWarningOpen(true);
    } else {
      setCommunity({ ...community, subscriptionType: communitySubscription });
    }
  };

  const handleConfirmDowngrade = () => {
    if (pendingSubscription) {
      const updatedFeatures = disableIncompatibleFeatures();

      setCommunity({
        ...community,
        subscriptionType: pendingSubscription,
        features: updatedFeatures,
        connectedMembers: false,
      });
    }

    setDowngradeWarningOpen(false);
    setPendingSubscription(null);
  };

  const handleCancelDowngrade = () => {
    setDowngradeWarningOpen(false);
    setPendingSubscription(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    if (selectedBusinessId) {
      await communityService.createRelatedCommunity(selectedBusinessId, community);
      navigate('/my-communities');
    }
    setLoading(false);
  };

  return (
    <>
      <StepFlowStep
        setStep={setStep}
        disabled={!community.subscriptionType || loading}
        showNext={!auth}
        onSubmit={auth ? handleSubmit : undefined}
        submitText={
          auth
            ? t('page.onboarding.confirmation.submit', {
                dashboard_type: 'community',
                dashboard_type_cap: 'Community',
              })
            : undefined
        }
        title={t('page.onboarding.subscription.title')}
        subtitle={t('page.onboarding.subscription.subtitle')}
        className="max-w-6xl">
        <SubscriptionSelector
          selectedSubscription={community.subscriptionType}
          mode="selectable"
          onSubscriptionSelect={handleSubscriptionSelection}
          showTrialPeriod={!selectedBusinessId}
          trialDays={community.affiliateCode ? 60 : 30}
          isBusiness={false}
          isSubCommunity={!!selectedBusinessId}
        />

        <div className="mt-4 rounded-xl bg-primary-100 p-4">
          <div className="flex items-start gap-3">
            <HiOutlineInformationCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-900" />
            <div>
              <p className="text-sm font-semibold text-primary-900">
                {t('page.onboarding.subscription.subCommunityFreeTitle')}
              </p>
              <p className="mt-1 text-sm text-primary-900 text-opacity-70">
                {t('page.onboarding.subscription.subCommunityFreeNote')}
              </p>
            </div>
          </div>
        </div>

        {!communityMode && !selectedBusinessId && (
          <div className="mt-6">
            <div className="w-1/2">
              <p>{t(`general.affiliateCode`)}</p>
              <input
                type="text"
                value={community.affiliateCode ?? ''}
                placeholder={`${t(`general.affiliateCode`)}...`}
                onChange={(e) => setCommunity({ ...community, affiliateCode: e.target.value })}
                readOnly={params.get('code') !== null}
              />
            </div>
          </div>
        )}
      </StepFlowStep>

      <AreYouSureModal
        open={downgradeWarningOpen}
        setOpen={setDowngradeWarningOpen}
        title={t('page.onboarding.subscription.downgrade.title')}
        description={t('page.onboarding.subscription.downgrade.description')}
        confirmLabel={t('page.onboarding.subscription.downgrade.confirm')}
        cancelLabel={t('page.onboarding.subscription.downgrade.cancel')}
        onConfirm={handleConfirmDowngrade}
        onCancel={handleCancelDowngrade}
      />
    </>
  );
}
